import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  MonitorPlay,
  BookOpen,
  FolderOpen,
  PencilLine,
  Check,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";
import { SectionHeading } from "../components/PageBits";
import { Reveal, RevealLines } from "../components/Reveal";

const CHECKOUT = "https://pay.hotmart.com/T106758268D?off=8mksx5iz";

const INCLUDES = [
  { icon: MonitorPlay, t: "Filminas" },
  { icon: BookOpen, t: "Workbook" },
  { icon: FolderOpen, t: "Recursos complementarios" },
  { icon: PencilLine, t: "Ejercicios prácticos" },
];

const MODULES = [
  "Bienvenida",
  "¿Qué significa ser humano?™",
  "La Culpa™",
  "El Nervio Vago™",
  "La Mente Consciente y El Subconsciente™",
  "El Potencial Oculto de la Conciencia™",
  "El Mapa De La Conciencia™",
  "El Mapa De La Percepción De La Realidad™",
  "El Poder Invisible que Dirige tu Vida™",
  "La Batalla Por El Control De Tu Mente™",
  "La Sombra™",
];

const PRICE_FEATURES = [
  "Acceso de por vida a los 11 módulos actuales.",
  "Actualizaciones futuras de estos módulos incluidas.",
  "Acceso inmediato.",
];

export default function LibraryPage() {
  return (
    <>
      {/* 1. Hero — claro y concreto */}
      <section className="grain relative overflow-hidden bg-navy pt-40 pb-16 sm:pt-48 sm:pb-24" data-testid="library-hero">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-40 top-10 h-[480px] w-[480px] rounded-full bg-gold/10 blur-[150px]" />
        </div>
        <div className="container-luxe relative z-10">
          <Reveal>
            <p className="overline mb-7 flex items-center gap-4">
              <span className="h-px w-10 bg-gold" /> Biblioteca Premium
            </p>
          </Reveal>
          <h1 className="max-w-4xl font-serif text-4xl font-light leading-[1.04] tracking-tight text-cream sm:text-6xl lg:text-7xl">
            <RevealLines lines={["Todo tu recorrido,"]} delay={0.2} />
            <RevealLines lines={["en un solo lugar."]} lineClassName="italic text-gold" delay={0.32} />
          </h1>
          <div className="mt-8 max-w-2xl space-y-5">
            <Reveal delay={0.5}>
              <p className="text-base font-light leading-relaxed text-cream/75 sm:text-lg">
                Accede hoy a 11 módulos disponibles, diseñados como un recorrido completo para
                comprender, sanar e integrar una nueva forma de vivir.
              </p>
            </Reveal>
            <Reveal delay={0.6}>
              <p className="text-base font-light leading-relaxed text-cream/75 sm:text-lg">
                Tu acceso incluye estos 11 módulos de por vida y todas las actualizaciones futuras
                realizadas dentro de ellos.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.7}>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-cream/70">
              <span className="flex items-center gap-2"><Check size={15} className="text-gold" /> 11 módulos disponibles</span>
              <span className="flex items-center gap-2"><Check size={15} className="text-gold" /> Acceso de por vida</span>
              <span className="flex items-center gap-2"><Check size={15} className="text-gold" /> Acceso inmediato</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2. Qué incluye cada módulo */}
      <section className="relative bg-cream py-24 text-navy sm:py-32" data-testid="library-includes">
        <div className="container-luxe">
          <SectionHeading dark={false} overline="Estructura de cada módulo" title="Qué incluye" accent="cada módulo." />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {INCLUDES.map((it, i) => (
              <Reveal key={it.t} delay={i * 0.08}>
                <div className="flex h-full flex-col gap-5 rounded-sm border border-navy/10 bg-white/60 p-8" data-testid={`library-include-${i}`}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold-deep/40 text-gold-deep">
                    <it.icon size={20} />
                  </span>
                  <h3 className="font-serif text-2xl leading-tight">{it.t}</h3>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-2xl text-sm font-light leading-relaxed text-navy/60">
              Esta estructura se repite en cada uno de los módulos del recorrido.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 3. El recorrido completo */}
      <section className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="library-modules">
        <div className="container-luxe relative z-10">
          <SectionHeading overline="El recorrido completo" title="Once módulos," accent="un mismo recorrido." />
          <div className="grid grid-cols-1 gap-x-12 md:grid-cols-2">
            {MODULES.map((m, i) => (
              <Reveal key={m} delay={(i % 6) * 0.06}>
                <div
                  className="group flex items-center gap-5 border-b border-gold/15 py-6"
                  data-testid={`library-module-${i + 1}`}
                >
                  <span className="font-serif text-2xl text-gold/70">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-serif text-2xl font-normal leading-tight text-cream transition-colors group-hover:text-gold sm:text-[1.7rem]">
                    {m}
                  </h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Relación con el resto de la Academia */}
      <section className="relative bg-cream py-24 text-navy sm:py-32" data-testid="library-relation">
        <div className="container-luxe">
          <SectionHeading dark={false} overline="Dentro de la Academia" title="Cómo se conecta" accent="con el resto." />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Reveal>
              <Link
                to="/academy"
                className="group flex h-full flex-col justify-between gap-8 rounded-sm border border-navy/10 bg-white/60 p-8 transition-colors hover:border-gold-deep/40"
                data-testid="library-link-academy"
              >
                <p className="text-lg font-light leading-relaxed text-navy/80">
                  La metodología se explica en la página <span className="text-gold-deep">Academia</span>.
                </p>
                <span className="flex items-center gap-2 font-serif text-xl text-gold-deep">
                  Ir a Academia
                  <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to="/programs"
                className="group flex h-full flex-col justify-between gap-8 rounded-sm border border-navy/10 bg-white/60 p-8 transition-colors hover:border-gold-deep/40"
                data-testid="library-link-programs"
              >
                <p className="text-lg font-light leading-relaxed text-navy/80">
                  El acompañamiento se desarrolla en la página <span className="text-gold-deep">Programas</span>.
                </p>
                <span className="flex items-center gap-2 font-serif text-xl text-gold-deep">
                  Ir a Programas
                  <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 5. Precio */}
      <section className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="library-price">
        <div className="container-luxe relative z-10">
          <SectionHeading overline="Acceso" title="Un pago." accent="Acceso para siempre." />
          <Reveal>
            <div className="mx-auto max-w-xl rounded-sm border border-gold/30 bg-gold/5 p-10 text-center">
              <p className="overline">Biblioteca Premium</p>
              <p className="mt-4 font-serif text-6xl font-light text-cream sm:text-7xl">
                $49 <span className="align-top text-2xl text-gold">USD</span>
              </p>
              <p className="mt-2 text-sm font-light text-cream/60">Pago único</p>

              <ul className="mx-auto mt-8 flex max-w-xs flex-col gap-3 text-left">
                {PRICE_FEATURES.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm font-light text-cream/85">
                    <Check size={16} className="shrink-0 text-gold" /> {f}
                  </li>
                ))}
              </ul>

              <a
                href={CHECKOUT}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold mt-10 w-full"
                data-testid="library-price-cta"
              >
                Comenzar ahora <ArrowRight size={15} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6. Cierre */}
      <section className="grain relative overflow-hidden bg-navy-deep py-24 sm:py-32" data-testid="library-closing">
        <div className="container-luxe relative z-10 max-w-3xl">
          <Reveal>
            <h2 className="font-serif text-4xl font-light leading-[1.08] tracking-tight text-cream sm:text-5xl">
              Hoy comienzas con una <span className="italic text-gold">biblioteca sólida</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base font-light leading-relaxed text-cream/75 sm:text-lg">
              La Biblioteca Premium podrá seguir creciendo con nuevos recursos, programas y
              experiencias.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-5 text-base font-light leading-relaxed text-cream/75 sm:text-lg">
              Tu compra incluye acceso permanente a los 11 módulos disponibles hoy y a las futuras
              mejoras realizadas dentro de esos mismos contenidos.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 font-serif text-xl font-light italic leading-snug text-gold sm:text-2xl">
              Los nuevos productos o programas creados en el futuro podrán ofrecerse por separado o
              mediante una membresía.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <a
              href={CHECKOUT}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold mt-10"
              data-testid="library-closing-cta"
            >
              Comenzar ahora <ArrowRight size={15} />
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
}
